import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, AlertTriangle, Calendar, ChevronRight } from "lucide-react";
import { api } from "../lib/api";

const ROUTE_FOR_KIND = { match: "/matches", tournament: "/tournaments", league: "/leagues", lesson: "/lessons" };

/**
 * Coach analytics tile — shows confirmation rate for upcoming lessons in next `windowHours` and
 * highlights any lesson where confirmations are below 50% so the coach can nudge attendees early.
 *
 * Props:
 *   windowHours?: number (default 48)
 *   kind?: "lesson" | "match" | "tournament" | "league" | "any" (default "lesson")
 */
export default function CoachAnalyticsTile({ windowHours = 48, kind = "lesson" }) {
  const [items, setItems] = useState([]);
  const [details, setDetails] = useState({}); // id -> attendance

  useEffect(() => {
    let mounted = true;
    api.get(`/me/upcoming?window_hours=${windowHours}`)
      .then(async (r) => {
        const filtered = (r.data || []).filter((it) => (kind === "any" ? true : it.kind === kind) && it.is_owner);
        if (!mounted) return;
        setItems(filtered);
        const entries = await Promise.all(filtered.map(async (it) => {
          try {
            const { data } = await api.get(`/events/${it.kind}/${it.id}/attendance`);
            return [it.id, data];
          } catch { return [it.id, null]; }
        }));
        if (mounted) setDetails(Object.fromEntries(entries));
      })
      .catch(() => mounted && setItems([]));
    return () => { mounted = false; };
  }, [windowHours, kind]);

  if (items.length === 0) return null;

  const totalReg = items.reduce((a, it) => a + (details[it.id]?.registered || 0), 0);
  const totalConfirmed = items.reduce((a, it) => a + (details[it.id]?.confirmed || 0), 0);
  const overallPct = totalReg > 0 ? Math.round((totalConfirmed / totalReg) * 100) : 0;
  const atRisk = items.filter((it) => {
    const d = details[it.id];
    if (!d || d.registered === 0) return false;
    return (d.confirmed / d.registered) < 0.5;
  });

  return (
    <div className="pp-card-elevated p-5" data-testid="coach-analytics-tile">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Next {windowHours}h · attendance</div>
          <div className="font-heading text-2xl font-black mt-1 flex items-center gap-2" data-testid="coach-analytics-pct">
            <TrendingUp size={18} className={overallPct >= 75 ? "text-[#00E676]" : overallPct >= 40 ? "text-[#FFD60A]" : "text-[#FF3B30]"} />
            {overallPct}% confirmed
          </div>
          <div className="text-xs text-zinc-500 mt-1">{totalConfirmed}/{totalReg} players across {items.length} {kind}{items.length === 1 ? "" : "s"}</div>
        </div>
        <div className={`shrink-0 w-14 h-14 rounded-full ring-2 ${overallPct >= 75 ? "ring-[#00E676]" : overallPct >= 40 ? "ring-[#FFD60A]" : "ring-[#FF3B30]/60"} flex items-center justify-center font-bold`}>
          {overallPct}%
        </div>
      </div>

      {atRisk.length > 0 && (
        <div className="mt-3 pt-3 border-t border-white/5" data-testid="coach-analytics-atrisk">
          <div className="text-xs flex items-center gap-1 text-[#FFD60A] uppercase tracking-wider mb-2">
            <AlertTriangle size={12} /> {atRisk.length} at-risk
          </div>
          <div className="space-y-2">
            {atRisk.map((it) => {
              const d = details[it.id] || {};
              const pct = d.registered ? Math.round((d.confirmed / d.registered) * 100) : 0;
              const route = ROUTE_FOR_KIND[it.kind] || `/${it.kind}s`;
              return (
                <Link key={it.id} to={`${route}/${it.id}`} data-testid={`coach-analytics-row-${it.id}`}
                  className="flex items-center justify-between gap-2 pp-card p-2 hover:border-[#FFD60A]/40 transition-colors">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{it.title}</div>
                    <div className="text-[11px] text-zinc-500 flex items-center gap-1">
                      <Calendar size={10} /> {it.start ? new Date(it.start).toLocaleString(undefined, { weekday: "short", hour: "2-digit", minute: "2-digit" }) : "—"} · {d.confirmed || 0}/{d.registered || 0} confirmed
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-zinc-400">
                    <span className="text-xs font-bold text-[#FFD60A]">{pct}%</span>
                    <ChevronRight size={14} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
